import React, {useEffect, useMemo, useState} from "react";
import Styles from './styles.module.scss';
import {useDispatch, useSelector} from "react-redux";
import {useTranslation} from "react-i18next";
import {getLeaderboard} from "../../store/cabinet/action";
import {formatNumber} from "../cabinetShell/config";

import heroArt from '../../theme/assets/redesign/leaderboard/hero.webp';
import avatarPlaceholder from '../../theme/assets/redesign/leaderboard/avatar_placeholder.webp';
import crownGold from '../../theme/assets/redesign/leaderboard/crown_gold.svg';
import crownSilver from '../../theme/assets/redesign/leaderboard/crown_silver.svg';
import crownBronze from '../../theme/assets/redesign/leaderboard/crown_bronze.svg';
import searchIcon from '../../theme/assets/redesign/leaderboard/search.svg';


// Фильтры уходят на бэк query-параметрами: ?period=all|month|week, ?type=referral
const FILTERS = [
    {key: 'all', label: 'За всё время', params: {period: 'all'}},
    {key: 'month', label: 'За месяц', params: {period: 'month'}},
    {key: 'week', label: 'За неделю', params: {period: 'week'}},
    {key: 'referral', label: 'Рефералы', params: {type: 'referral'}},
];

const CROWNS = {1: crownGold, 2: crownSilver, 3: crownBronze};

// количества кругов в ответе API может не быть — пробуем известные варианты
const getRounds = (item) => item?.rounds ?? item?.laps_count ?? item?.laps ?? null;


const CabinetLeaderboardMain = () => {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const leaderboard = useSelector(store => store.cabinet.leaderboard);
    const userData = useSelector(store => store.cabinet.userData);

    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');

    useEffect(() => {
        const current = FILTERS.find(f => f.key === filter);
        dispatch(getLeaderboard(current?.params));
    }, [dispatch, filter]);

    // позиции считаем до поиска, чтобы при фильтрации по нику место не менялось
    const rows = useMemo(() => {
        const list = Array.isArray(leaderboard?.leader_boards) ? leaderboard.leader_boards : [];
        const me = userData?.user;
        const withPos = list.map((item, index) => ({
            item,
            position: item?.position ?? index + 1,
            isMe: !!me && (
                (me.id !== undefined && item?.user?.id === me.id) ||
                (!!me.username && item?.user?.username === me.username)
            ),
        }));
        const q = search.trim().toLowerCase().replace(/^@/, '');
        return q ? withPos.filter(r => (r.item?.user?.username || '').toLowerCase().includes(q)) : withPos;
    }, [leaderboard, userData, search]);

    const isReferral = filter === 'referral';
    // результат строки: очки, а для вкладки «Рефералы» — число приглашённых
    const resultOf = (item) => isReferral ? item?.invited : item?.points;
    const resultUnit = isReferral ? t('приглаш.') : t('очков');

    // Моя позиция из `me`: если меня нет в топ-100, показываем отдельную строку под таблицей
    const me = leaderboard?.me;
    const meInList = Array.isArray(leaderboard?.leader_boards) && leaderboard.leader_boards.some(item =>
        !!userData?.user?.username && item?.user?.username === userData.user.username);
    const showMeRow = !search && !meInList && me && me.position !== null && me.position !== undefined;

    return (
        <section className={Styles.container}>
            {/* ===== Hero ===== */}
            <div className={Styles.hero}>
                <div className={Styles.heroArt}>
                    <img src={heroArt} alt=""/>
                </div>
                <div className={Styles.heading}>
                    <div className={Styles.title}>{t('Таблица лидеров')}</div>
                    <div className={Styles.subtitle}>{t('Лучшие участники сообщества и их результаты')}</div>
                </div>
                <div className={`${Styles.controls} ${Styles.desktopOnly}`}>
                    <Filters filter={filter} setFilter={setFilter} t={t}/>
                    <Search value={search} onChange={setSearch} t={t}/>
                </div>
            </div>

            {/* ===== Таблица ===== */}
            <div className={Styles.table}>
                <div className={`${Styles.controls} ${Styles.mobileOnly}`}>
                    <Filters filter={filter} setFilter={setFilter} t={t}/>
                    <Search value={search} onChange={setSearch} t={t}/>
                </div>

                <div className={Styles.header}>
                    <div className={Styles.colPosition}>{t('Позиция')}</div>
                    <div className={`${Styles.colAvatar} ${Styles.desktopOnly}`}/>
                    <div className={Styles.colNickname}>{t('Никнейм')}</div>
                    <div className={Styles.colResult}>{t('Результат')}</div>
                    <div className={Styles.colRounds}>{t('Круги')}</div>
                    <div className={Styles.colYou}/>
                </div>

                <div className={Styles.body}>
                    {rows.length ? rows.map(({item, position, isMe}) => {
                        const crown = CROWNS[position];
                        const rounds = getRounds(item);
                        return (
                            <div
                                key={item?.user?.id ?? `${position}-${item?.user?.username}`}
                                className={`${Styles.row} ${isMe ? Styles.rowMe : ''}`}
                            >
                                <div className={`${Styles.colPosition} ${crown ? '' : Styles.noCrown}`}>
                                    {crown ? <img className={Styles.crown} src={crown} alt=""/> : null}
                                    <span>{position}</span>
                                </div>
                                <div className={Styles.colAvatar}>
                                    <img src={item?.user?.avatar || avatarPlaceholder} alt=""/>
                                </div>
                                <div className={Styles.colNickname}>
                                    <span>@{item?.user?.username}</span>
                                </div>
                                <div className={Styles.colResult}>
                                    <span className={Styles.resultValue}>{formatNumber(resultOf(item), isReferral ? 0 : 2)}</span>
                                    <span className={Styles.resultUnit}>{resultUnit}</span>
                                </div>
                                <div className={Styles.colRounds}>{rounds !== null ? formatNumber(rounds, 0) : '—'}</div>
                                <div className={Styles.colYou}>
                                    {isMe ? <span className={Styles.youBadge}>{t('Вы')}</span> : null}
                                </div>
                            </div>
                        );
                    }) : (
                        <div className={Styles.empty}>{search ? t('Никого не нашли') : t('Пока нет участников')}</div>
                    )}
                </div>

                {showMeRow ? (
                    <div className={`${Styles.row} ${Styles.rowMe} ${Styles.rowMePinned}`}>
                        <div className={`${Styles.colPosition} ${Styles.noCrown}`}>
                            <span>{me.position}</span>
                        </div>
                        <div className={Styles.colAvatar}>
                            <img src={userData?.user?.avatar || avatarPlaceholder} alt=""/>
                        </div>
                        <div className={Styles.colNickname}>
                            <span>@{userData?.user?.username}</span>
                        </div>
                        <div className={Styles.colResult}>
                            <span className={Styles.resultValue}>{formatNumber(resultOf(me), isReferral ? 0 : 2)}</span>
                            <span className={Styles.resultUnit}>{resultUnit}</span>
                        </div>
                        <div className={Styles.colRounds}>{getRounds(me) !== null ? formatNumber(getRounds(me), 0) : '—'}</div>
                        <div className={Styles.colYou}>
                            <span className={Styles.youBadge}>{t('Вы')}</span>
                        </div>
                    </div>
                ) : null}
            </div>
        </section>
    );
};

const Filters = ({filter, setFilter, t}) => (
    <div className={Styles.filters}>
        {FILTERS.map(f => (
            <button
                type="button"
                key={f.key}
                className={`${Styles.chip} ${filter === f.key ? Styles.chipActive : ''}`}
                onClick={() => setFilter(f.key)}
            >
                {t(f.label)}
            </button>
        ))}
    </div>
);

const Search = ({value, onChange, t}) => (
    <label className={Styles.search}>
        <img src={searchIcon} alt="" width="18" height="18"/>
        <input
            type="text"
            value={value}
            placeholder={t('Поиск по никнейму…')}
            onChange={e => onChange(e.target.value)}
        />
    </label>
);

export default CabinetLeaderboardMain;
